/**
 * DisciplineHub — single entry tab that exposes all 11 palette-based
 * discipline editors per `Capexara Process Industry_ProcessPlant_Complete_Architecture.md`
 * §6.3. Lets the user pick a discipline and renders the shared
 * DisciplineCanvasEditor underneath.
 */
import React, { useState } from "react";
import { Zap, Shield, Cable, Cpu, Hammer, Columns, Ruler,
          Flame, Radio, Droplets, Layers } from "lucide-react";
import DisciplineCanvasEditor from "./DisciplineCanvasEditor";

const DISCIPLINES = [
  { key: "hazarea", title: "Haz Area (E.1)", icon: Shield, stage: "E.1" },
  { key: "sld", title: "SLD (E.3)", icon: Zap, stage: "E.3" },
  { key: "earthing", title: "Earthing (E.4)", icon: Zap, stage: "E.4" },
  { key: "cable_tray", title: "Cable Tray (E.5)", icon: Cable, stage: "E.5" },
  { key: "control_arch", title: "Control Arch (F.1)", icon: Cpu, stage: "F.1" },
  { key: "foundation", title: "Foundation (G.2)", icon: Hammer, stage: "G.2" },
  { key: "steel_ga", title: "Steel GA (G.3)", icon: Columns, stage: "G.3" },
  { key: "rc_detail", title: "RC Detail (G.4)", icon: Ruler, stage: "G.4" },
  { key: "fire_water", title: "Fire Water (H.1)", icon: Droplets, stage: "H.1" },
  { key: "detection", title: "Detection (H.2)", icon: Radio, stage: "H.2" },
  { key: "suppression", title: "Suppression (H.3)", icon: Flame, stage: "H.3" },
];

export default function DisciplineHub({ projectId, phaseVisibility }) {
  const [active, setActive] = useState("hazarea");
  const stages = phaseVisibility?.stages || {};

  return (
    <div data-testid="discipline-hub" style={{ display: "flex", flexDirection: "column",
                                                 height: "calc(100vh - 230px)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                     padding: 8, borderBottom: "1px solid var(--pf-border)",
                     background: "var(--pf-surface-2)" }}>
        <Layers size={14} color="var(--pf-accent)" />
        <span className="pf-overline" style={{ margin: 0 }}>DISCIPLINE EDITORS</span>
        <span style={{ fontSize: 11, color: "var(--pf-text-dim)",
                        fontFamily: "'JetBrains Mono', monospace", marginRight: 8 }}>
          · 11 palettes
        </span>
        {DISCIPLINES.map((d) => {
          const v = stages[d.stage];
          const dim = v?.state === "locked";
          const partial = v?.state === "partial";
          const Ico = d.icon;
          return (
            <button
              key={d.key}
              data-testid={`disc-pick-${d.key}`}
              onClick={() => setActive(d.key)}
              title={v?.note
                ? `${d.title} — ${v.state.toUpperCase()} in current phase. ${v.note}`
                : d.title}
              style={{
                background: active === d.key ? "var(--pf-accent)" : "var(--pf-surface)",
                color: active === d.key ? "#0E1218"
                  : dim ? "var(--pf-text-dim)" : "var(--pf-fg)",
                border: `1px solid ${active === d.key
                  ? "var(--pf-accent)"
                  : (partial ? "#fbbf24" : "var(--pf-border)")}`,
                padding: "4px 10px", fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace",
                borderRadius: 3, cursor: "pointer",
                opacity: dim && active !== d.key ? 0.55 : 1,
                display: "inline-flex", alignItems: "center", gap: 4,
                fontWeight: active === d.key ? 700 : 500,
              }}
            >
              <Ico size={11} />
              {d.title}
              {dim && <span style={{ fontSize: 9, marginLeft: 4 }}>LOCKED</span>}
              {partial && <span style={{ fontSize: 9, marginLeft: 4, color: "#fbbf24" }}>PART</span>}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        <DisciplineCanvasEditor projectId={projectId} disciplineKey={active} />
      </div>
    </div>
  );
}
